import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { changeBusinessAccount } from '@/features/auth/services/auth';
import type {
  ChangeBusinessAccountRequest,
  ChangeBusinessAccountResponse,
} from '@/features/auth/types/auth.types';
import { setAuthSession } from '@/shared/api/storage';
import { useWorkspaceStore } from '@/shared/store/workspace';
import { USER_INVITES_QUERY_KEY } from '@/features/users/hooks/useUserInvites';
import { BUSINESS_ACCOUNT_USERS_QUERY_KEY } from '@/features/users/hooks/useBusinessAccountUsers';
import { EXTERNAL_PROFILES_QUERY_KEY } from '@/features/users/hooks/useExternalProfiles';
import { useUserManagementStore } from '@/features/users/store';

/**
 * Mutation hook to switch the active business account (client).
 *
 * ## Flow
 * 1. Calls POST /auth/change-business-account with the new businessId
 * 2. Backend returns a new JWT scoped to the new business account
 * 3. onSuccess:
 *    a. Persists the new token to storage
 *    b. Updates workspace store with new client (resets entity)
 *    c. Purges user-management query caches (invites, users, profiles)
 *    d. Resets user-management UI store (pagination, filters, selections)
 *    e. Invalidates user-info cache → triggers refetch with new token context
 *    f. Navigates to home `/`
 *
 * The refetched user-info will contain the new `businessAccount` with its
 * platforms, and `useWorkspace` will auto-select the first entity.
 */
export function useChangeBusinessAccount() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<
    ChangeBusinessAccountResponse,
    Error,
    ChangeBusinessAccountRequest
  >({
    mutationFn: changeBusinessAccount,
    onSuccess: (data, variables) => {
      setAuthSession({
        accessToken: data.accessToken,
        expiresIn: data.expiresIn,
        tokenType: data.tokenType,
        scope: data.scope,
      });
      // Access store outside React lifecycle to avoid hook ordering issues
      useWorkspaceStore.getState().setClient(variables.businessId);

      // Purge stale user-management data scoped to the previous business account
      queryClient.removeQueries({ queryKey: [...USER_INVITES_QUERY_KEY] });
      queryClient.removeQueries({
        queryKey: [...BUSINESS_ACCOUNT_USERS_QUERY_KEY],
      });
      queryClient.removeQueries({
        queryKey: [...EXTERNAL_PROFILES_QUERY_KEY],
      });
      useUserManagementStore.getState().reset();

      queryClient.invalidateQueries({ queryKey: ['userInfo'] });
      navigate('/', { replace: true });
    },
  });
}
